import Phaser from 'phaser';
import type { Action } from '../control/actions';
import { TouchSource, GamepadSource } from '../control/ControlMap';

// HUD — the on-screen touch ControlMap surface plus a small status readout. The
// four touch buttons write into a TouchSource (the third input source alongside
// keyboard and gamepad); the readout shows the live input source + Actor State
// so the slice is legible while verifying. Fixed to the camera (scrollFactor 0).

interface PadButton {
  action: Action;
  gfx: Phaser.GameObjects.Arc;
  label: Phaser.GameObjects.Text;
}

export class Hud {
  private scene: Phaser.Scene;
  private touch: TouchSource;
  private buttons: PadButton[] = [];
  private status: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, touch: TouchSource) {
    this.scene = scene;
    this.touch = touch;

    // Extra touch pointers so left+right-thumb presses register together.
    scene.input.addPointer(2);

    const { width, height } = scene.scale;
    const y = height - 46;

    // Movement cluster (left), action cluster (right).
    this.addButton('left', 44, y, '◀');
    this.addButton('right', 104, y, '▶');
    this.addButton('attack', width - 44, y, '✊');
    this.addButton('jump', width - 104, y, '⤒');
    this.addButton('up', 164, y - 30, '▲');
    this.addButton('down', 164, y + 18, '▼');

    this.status = scene.add
      .text(8, 8, '', {
        fontFamily: 'ui-monospace, Menlo, monospace',
        fontSize: '11px',
        color: '#d8c05d',
      })
      .setScrollFactor(0)
      .setDepth(1000);
  }

  private addButton(action: Action, x: number, y: number, glyph: string): void {
    const gfx = this.scene.add
      .circle(x, y, 26, 0xffffff, 0.08)
      .setStrokeStyle(1, 0xd8c05d, 0.6)
      .setScrollFactor(0)
      .setDepth(1000)
      .setInteractive({ useHandCursor: true });

    const label = this.scene.add
      .text(x, y, glyph, { fontFamily: 'sans-serif', fontSize: '20px', color: '#e2e2e0' })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1001);

    const press = () => { this.touch.set(action, true); gfx.setFillStyle(0xd8c05d, 0.35); };
    const release = () => { this.touch.set(action, false); gfx.setFillStyle(0xffffff, 0.08); };

    gfx.on('pointerdown', press);
    gfx.on('pointerup', release);
    gfx.on('pointerout', release);
    gfx.on('pointerupoutside', release);

    this.buttons.push({ action, gfx, label });
  }

  /** Refresh the status readout. `state` is the player Actor's current State. */
  update(state: string): void {
    const pad = GamepadSource.anyConnected() ? 'gamepad ✓' : 'gamepad —';
    this.status.setText(
      `18 KNEES — arcade slice\n` +
      `walk ◀▶ A D · depth ▲▼ W S · jump Space · attack J\n` +
      `${pad}   state: ${state}`,
    );
  }
}
